import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient } from '../api/client';

// ── Type Definitions ──────────────────────────────────────────

interface ExecutiveTitle {
  id: string;
  name: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface ApiError {
  statusCode?: number;
  message?: string;
}

// ── Inline Styles ─────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  section: {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    color: '#0f2d1f',
    fontSize: '1.2rem',
    marginBottom: '1rem',
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.25rem',
  },
  tableWrapper: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' },
  th: {
    textAlign: 'left' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '2px solid #e0e0e0',
    fontWeight: 600,
    color: '#0f2d1f',
    fontSize: '0.85rem',
  },
  td: {
    padding: '0.75rem 0.5rem',
    borderBottom: '1px solid #e0e0e0',
    fontSize: '0.85rem',
    color: '#333',
  },
  btn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
  btnSmall: {
    padding: '0.3rem 0.6rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: '0.25rem',
  },
  btnDanger: {
    padding: '0.3rem 0.6rem',
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: '0.25rem',
  },
  btnGold: {
    padding: '0.3rem 0.6rem',
    backgroundColor: '#c9a84c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  input: {
    padding: '0.4rem 0.6rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  addForm: {
    padding: '1rem',
    backgroundColor: '#f9f9f9',
    borderRadius: '6px',
    marginBottom: '1rem',
    border: '1px solid #e0e0e0',
  },
  formRow: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'flex-end',
    flexWrap: 'wrap' as const,
  },
  field: { display: 'flex', flexDirection: 'column' as const, gap: '0.25rem' },
  label: { fontWeight: 600, fontSize: '0.75rem', color: '#333' },
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  dialog: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '2rem',
    maxWidth: '400px',
    width: '90%',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  },
  dialogTitle: {
    color: '#d32f2f',
    fontSize: '1.1rem',
    marginBottom: '1rem',
    fontWeight: 600,
  },
  dialogText: { fontSize: '0.9rem', color: '#333', marginBottom: '1.5rem', lineHeight: 1.5 },
  dialogActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' },
};

// ── Component ─────────────────────────────────────────────────

export function ExecutiveTitlesManager() {
  const [titles, setTitles] = useState<ExecutiveTitle[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addName, setAddName] = useState('');
  const [addSortOrder, setAddSortOrder] = useState('0');

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editSortOrder, setEditSortOrder] = useState('');

  // Delete confirmation dialog state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    name: string;
    memberCount?: number;
  } | null>(null);

  // ── Data Fetching ─────────────────────────────────────────────

  async function fetchTitles() {
    try {
      const data = await apiClient<ExecutiveTitle[]>('/executive-titles');
      setTitles(data);
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setError(apiErr.message || 'Failed to load executive titles.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchTitles();
  }, []);

  // ── Actions ───────────────────────────────────────────────────

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    const trimmedName = addName.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }
    try {
      await apiClient('/executive-titles', {
        method: 'POST',
        body: {
          name: trimmedName,
          sortOrder: parseInt(addSortOrder, 10) || 0,
        },
      });
      setAddName('');
      setAddSortOrder('0');
      setShowAddForm(false);
      setError('');
      fetchTitles();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setError(apiErr.message || 'Failed to create executive title.');
    }
  }

  function startEdit(title: ExecutiveTitle) {
    setEditingId(title.id);
    setEditName(title.name);
    setEditSortOrder(String(title.sortOrder));
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName('');
    setEditSortOrder('');
  }

  async function handleSaveEdit() {
    if (!editingId) return;
    const trimmedName = editName.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }
    try {
      await apiClient(`/executive-titles/${editingId}`, {
        method: 'PATCH',
        body: {
          name: trimmedName,
          sortOrder: parseInt(editSortOrder, 10) || 0,
        },
      });
      cancelEdit();
      setError('');
      fetchTitles();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setError(apiErr.message || 'Failed to update executive title.');
    }
  }

  async function handleDelete(id: string, name: string) {
    try {
      await apiClient(`/executive-titles/${id}`, { method: 'DELETE' });
      setDeleteConfirm(null);
      setError('');
      fetchTitles();
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      if (apiErr.statusCode === 409) {
        // Extract member count from the error message
        const match = apiErr.message?.match(/(\d+)\s+member/);
        const memberCount = match ? parseInt(match[1], 10) : undefined;
        setDeleteConfirm({ id, name, memberCount });
      } else {
        setError(apiErr.message || 'Failed to delete executive title.');
      }
    }
  }

  async function confirmForceDelete() {
    // The API doesn't support force delete — this dialog just informs the user
    // Close the dialog since the title cannot be deleted while in use
    setDeleteConfirm(null);
  }

  // ── Render ────────────────────────────────────────────────────

  return (
    <div style={styles.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ ...styles.sectionTitle, marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
          Executive Titles
        </h2>
        <button style={styles.btn} onClick={() => setShowAddForm(!showAddForm)}>
          + Add Title
        </button>
      </div>

      {error && (
        <div style={styles.error} onClick={() => setError('')}>
          {error} <span style={{ float: 'right', fontWeight: 700 }}>×</span>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <form onSubmit={handleAdd} style={styles.addForm}>
          <div style={styles.formRow}>
            <div style={styles.field}>
              <label style={styles.label}>Name *</label>
              <input
                style={{ ...styles.input, width: '200px' }}
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder="e.g. President"
                maxLength={100}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Sort Order</label>
              <input
                style={{ ...styles.input, width: '80px' }}
                type="number"
                value={addSortOrder}
                onChange={(e) => setAddSortOrder(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button type="submit" style={styles.btnSmall}>Add</button>
              <button type="button" style={styles.btnGold} onClick={() => setShowAddForm(false)}>Cancel</button>
            </div>
          </div>
        </form>
      )}

      {/* Titles Table */}
      {loading ? (
        <p style={{ color: '#666' }}>Loading...</p>
      ) : titles.length === 0 ? (
        <p style={{ color: '#666' }}>No executive titles defined yet.</p>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Sort Order</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {titles.map((title) =>
                editingId === title.id ? (
                  <tr key={title.id}>
                    <td style={styles.td}>
                      <input
                        style={{ ...styles.input, width: '180px' }}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        maxLength={100}
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        style={{ ...styles.input, width: '70px' }}
                        type="number"
                        value={editSortOrder}
                        onChange={(e) => setEditSortOrder(e.target.value)}
                      />
                    </td>
                    <td style={styles.td}>
                      <button style={styles.btnSmall} onClick={handleSaveEdit}>Save</button>
                      <button style={styles.btnGold} onClick={cancelEdit}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={title.id}>
                    <td style={styles.td}>{title.name}</td>
                    <td style={styles.td}>{title.sortOrder}</td>
                    <td style={styles.td}>
                      <button style={styles.btnSmall} onClick={() => startEdit(title)}>Edit</button>
                      <button style={styles.btnDanger} onClick={() => handleDelete(title.id, title.name)}>Delete</button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div style={styles.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.dialogTitle}>Cannot Delete Title</h3>
            <p style={styles.dialogText}>
              The executive title <strong>"{deleteConfirm.name}"</strong> is currently assigned to{' '}
              <strong>{deleteConfirm.memberCount ?? 'one or more'}</strong> member
              {deleteConfirm.memberCount !== 1 ? 's' : ''}.
              <br /><br />
              Please reassign or remove this title from all members before deleting it.
            </p>
            <div style={styles.dialogActions}>
              <button
                style={{ ...styles.btn, backgroundColor: '#666' }}
                onClick={confirmForceDelete}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
